import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, CreditCard, Globe, MessageSquare, Sparkles, Users } from 'lucide-react';
import { PlanRunsPanel } from '../../ai-plans/PlanWorkspace';
import AdviserWorkspaceHeader from '../../advisers/AdviserWorkspaceHeader';
import { adviserApi, type PracticeSummary, type PracticeWorkspace } from '../../advisers/api';
import AdviserClientsPanel from './AdviserClientsPanel';
import AdviserTeamPanel from './AdviserTeamPanel';
import AdviserProfileEditor from './AdviserProfileEditor';
import AdviserBillingPanel from './AdviserBillingPanel';
import AdviserObligationsPanel from './AdviserObligationsPanel';
import ClientAdviserAccessPanel from './ClientAdviserAccessPanel';
import AdviserEnquiriesPanel from './AdviserEnquiriesPanel';
import '../../advisers/Advisers.css';

type Tab = 'clients' | 'team' | 'billing' | 'profile' | 'enquiries' | 'ai-plans';
export default function AdviserWorkspacePage() {
  const [params, setParams] = useSearchParams();
  const [practices, setPractices] = useState<PracticeSummary[]>([]);
  const [workspace, setWorkspace] = useState<PracticeWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const practiceId = params.get('practice') || practices[0]?.id || '';
  const tab = (params.get('tab') || 'clients') as Tab;
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    adviserApi
      .practices()
      .then((data) => {
        if (active) setPractices(data);
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [retry]);
  const refreshWorkspace = useCallback(async () => {
    if (!practiceId) return;
    const data = await adviserApi.workspace(practiceId);
    setWorkspace(data);
    setPractices((current) =>
      current.map((practice) => (practice.id === data.practice.id ? data.practice : practice))
    );
  }, [practiceId]);
  useEffect(() => {
    if (!practiceId) {
      setWorkspace(null);
      return;
    }
    let active = true;
    setLoading(true);
    setWorkspace(null);
    setError('');
    adviserApi
      .workspace(practiceId)
      .then((data) => {
        if (active) setWorkspace(data);
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [practiceId, retry]);
  const selectTab = (next: Tab) => {
    const newParams = new URLSearchParams(params);
    newParams.set('tab', next);
    setParams(newParams);
  };
  const practice = workspace?.practice;
  const approved = practice?.status === 'approved';
  const owner = practice?.role === 'owner';
  const allowedTab: Tab =
    owner && tab === 'enquiries'
      ? 'enquiries'
      : approved
        ? !owner && !['clients', 'ai-plans'].includes(tab)
          ? 'clients'
          : ['clients', 'team', 'billing', 'profile', 'ai-plans'].includes(tab)
            ? tab
            : 'clients'
        : owner && tab === 'billing'
          ? 'billing'
          : 'profile';
  return (
    <div className="adviser-workspace">
      <AdviserWorkspaceHeader
        title="Adviser workspace"
        subtitle="Your practice, clients and public presence"
      />
      <div className="adviser-workspace-body">
        <aside className="adviser-sidebar">
          <Link to="/dashboard" className="adviser-text-link">
            ← My companies
          </Link>
          <p className="adviser-eyebrow">Adviser practice</p>
          {practices.length > 1 ? (
            <label>
              Switch practice
              <select
                aria-label="Switch practice"
                value={practiceId}
                onChange={(event) => {
                  setParams({ practice: event.target.value, tab: 'clients' });
                }}
              >
                {practices.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <h2>{practice?.name || practices[0]?.name || 'Build your practice'}</h2>
          )}
          {practice && (
            <>
              <span className={`adviser-status adviser-status-${practice.status}`}>
                {practice.status}
              </span>
              <p className="adviser-muted">
                {practice.role === 'owner' ? 'Practice owner' : 'Assigned adviser'}
              </p>
              <nav aria-label="Adviser workspace">
                {approved && (
                  <button
                    type="button"
                    aria-current={allowedTab === 'clients' ? 'page' : undefined}
                    onClick={() => selectTab('clients')}
                  >
                    <Building2 size={18} />
                    Clients
                  </button>
                )}
                {approved && owner && (
                  <>
                    <button
                      type="button"
                      aria-current={allowedTab === 'team' ? 'page' : undefined}
                      onClick={() => selectTab('team')}
                    >
                      <Users size={18} />
                      Team
                    </button>
                  </>
                )}
                {approved && (
                  <button
                    type="button"
                    aria-current={allowedTab === 'ai-plans' ? 'page' : undefined}
                    onClick={() => selectTab('ai-plans')}
                  >
                    <Sparkles size={18} />
                    AI plans
                  </button>
                )}
                {owner && (
                  <button
                    type="button"
                    aria-current={allowedTab === 'enquiries' ? 'page' : undefined}
                    onClick={() => selectTab('enquiries')}
                  >
                    <MessageSquare size={18} />
                    Enquiries
                  </button>
                )}
                {owner && (
                  <button
                    type="button"
                    aria-current={allowedTab === 'billing' ? 'page' : undefined}
                    onClick={() => selectTab('billing')}
                  >
                    <CreditCard size={18} />
                    Billing
                  </button>
                )}
                {owner && (
                  <button
                    type="button"
                    aria-current={allowedTab === 'profile' ? 'page' : undefined}
                    onClick={() => selectTab('profile')}
                  >
                    <Globe size={18} />
                    Public Profile
                  </button>
                )}
              </nav>
            </>
          )}
          <div className="adviser-sidebar-note">
            <p>Clear client boundaries</p>
            <span>Each company retains its own ownership, data and direct access.</span>
          </div>
        </aside>
        <main className="adviser-workspace-main">
          <ClientAdviserAccessPanel />
          {loading ? (
            <p className="adviser-empty" role="status">
              Loading your practice…
            </p>
          ) : error ? (
            <div className="adviser-error" role="alert">
              {error}
              <button type="button" onClick={() => setRetry((value) => value + 1)}>
                Try again
              </button>
            </div>
          ) : !practiceId ? (
            <section className="adviser-application">
              <p className="adviser-eyebrow">The adviser programme</p>
              <h1>More clients. A clearer way to work.</h1>
              <p>
                Manage client strategy in one practice workspace, give your team the right access
                and introduce your firm through a public profile.
              </p>
              <form
                className="adviser-panel"
                onSubmit={async (event) => {
                  event.preventDefault();
                  setApplying(true);
                  setError('');
                  const form = new FormData(event.currentTarget);
                  try {
                    const created = await adviserApi.apply({
                      name: String(form.get('name')),
                      website: String(form.get('website')),
                      billingEmail: String(form.get('billingEmail')),
                      applicationNotes: String(form.get('applicationNotes')),
                    });
                    setPractices((current) => [...current, created]);
                    setParams({ practice: created.id, tab: 'profile' });
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Application failed.');
                  } finally {
                    setApplying(false);
                  }
                }}
              >
                <h2>Apply for your firm</h2>
                <p>
                  Onsavii reviews each application before activating client management. You can
                  prepare your public profile while you wait.
                </p>
                <fieldset disabled={applying}>
                  <div className="adviser-form-grid">
                    <label>
                      Firm name
                      <input name="name" required maxLength={200} />
                    </label>
                    <label>
                      Firm website
                      <input name="website" type="url" placeholder="https://…" />
                    </label>
                    <label className="adviser-form-full">
                      Billing contact email
                      <input name="billingEmail" type="email" required />
                    </label>
                    <label className="adviser-form-full">
                      Tell us about your advisory work
                      <textarea
                        name="applicationNotes"
                        rows={5}
                        required
                        maxLength={6000}
                        placeholder="Who you help, how you work, and what you would like to do with StratPlan"
                      />
                    </label>
                  </div>
                </fieldset>
                <button className="adviser-button" type="submit" disabled={applying}>
                  {applying ? 'Submitting…' : 'Submit application'}
                </button>
              </form>
            </section>
          ) : (
            workspace && (
              <>
                {!approved && (
                  <div
                    className={`adviser-notice ${practice?.status === 'suspended' ? 'adviser-error' : ''}`}
                  >
                    <strong>
                      {practice?.status === 'pending'
                        ? 'Your practice application is being reviewed.'
                        : practice?.status === 'rejected'
                          ? 'Your practice application has not been approved.'
                          : 'Your practice is suspended.'}
                    </strong>
                    <p>
                      {practice?.status === 'pending'
                        ? 'Prepare your public profile now. Client access opens after approval.'
                        : 'Client delegation and public visibility are unavailable. Contact the Onsavii team to review your practice status.'}
                    </p>
                  </div>
                )}
                {allowedTab === 'clients' && approved && (
                  <AdviserClientsPanel
                    practiceId={practiceId}
                    clients={workspace.clients}
                    members={workspace.team.members}
                    canManage={owner}
                    onChanged={refreshWorkspace}
                  />
                )}
                {allowedTab === 'ai-plans' && approved && (
                  <PlanRunsPanel key={practiceId} practiceId={practiceId} />
                )}
                {allowedTab === 'team' && approved && owner && (
                  <AdviserTeamPanel
                    practiceId={practiceId}
                    members={workspace.team.members}
                    invitations={workspace.team.invitations}
                    onChanged={refreshWorkspace}
                  />
                )}
                {allowedTab === 'billing' && approved && owner && (
                  <AdviserBillingPanel practiceId={practiceId} clients={workspace.clients} />
                )}
                {allowedTab === 'billing' && !approved && owner && (
                  <AdviserObligationsPanel practiceId={practiceId} />
                )}
                {allowedTab === 'profile' && owner && (
                  <AdviserProfileEditor
                    practiceId={practiceId}
                    practiceName={practice.name}
                    profile={workspace.profile}
                    onSaved={(profile) =>
                      setWorkspace((current) => (current ? { ...current, profile } : current))
                    }
                  />
                )}
                {allowedTab === 'enquiries' && owner && (
                  <AdviserEnquiriesPanel key={practiceId} practiceId={practiceId} />
                )}
              </>
            )
          )}
        </main>
      </div>
    </div>
  );
}
